import {
  DataEntryOption,
  DetailRequirement,
  EngagementTaskTrait,
  ProgramTask,
  ResourceDataType,
  RPDataEntry,
  TraitDisplaysItem,
} from '@cibo/core'
import { Markdown } from '@cibo/ui'
import { Stack } from '@mui/material'
import { propEq } from 'ramda'
import { ReactElement, useCallback, useState } from 'react'
import { useProgramEngagementContext } from '../../../../context'
import { TaskEditorComponentProps } from '../../TaskEditorComponent/types'
import { DataEntryCheckboxList } from './DataEntryCheckboxList'
import { DataEntryFileUpload } from './DataEntryFileUpload'
import { DataEntryGateGroup } from './DataEntryGateGroup'
import { DataEntryRadioGroup } from './DataEntryRadioGroup'
import { DataEntryTilesSelector } from './DataEntryTilesSelector'
import { DatePickerTraitEditor } from './DatePickerTraitEditor'
import { EmailDataEntryField } from './EmailDataEntryField'
import { LongTextDataEntryField } from './LongTextDataEntryField'
import { NumberDataEntryField } from './NumberDataEntryField'
import { TaskDataUpdates } from './types'

type SingleTargetTraitEditorProps = {
  detailRequirements?: DetailRequirement[]
  taskConfig: ProgramTask<RPDataEntry>
  taskId: string
  traitConfig: EngagementTaskTrait
  resourceId: string
  saveTaskData: (updates: TaskDataUpdates, options?: { onError?: (error: Error) => void }) => void
  traitDisplay?: TraitDisplaysItem
  TaskEditorComponent(props: TaskEditorComponentProps): ReactElement
  SingleTargetTraitDisplays(props: any): ReactElement | ReactElement[] | null
}

// eslint-disable-next-line complexity
export const SingleTargetTraitEditor = ({
  detailRequirements,
  taskConfig,
  taskId,
  traitConfig,
  saveTaskData,
  resourceId,
  traitDisplay,
  TaskEditorComponent,
  SingleTargetTraitDisplays,
}: SingleTargetTraitEditorProps) => {
  const { engagementId, engagement, taskData } = useProgramEngagementContext()
  const firstRequirement = detailRequirements?.[0]
  const traitDetail = taskData.data?.getDetail({ traitConfig, resourceId })
  const ciboSourced = traitDetail?.source === 'cibo'
  const traitDetailValue = taskData.data?.getDetailValue({
    traitConfig,
    detailRequirement: firstRequirement,
    resourceId,
  })
  const [saveError, setSaveError] = useState<Error | undefined>()

  // @TODO: require so we don't have to default
  const resolvedDataType = traitConfig.dataType || 'field'

  /** NOTE: This component encapsulates the translation between field style details
   * and the value-based user & workflow details so that the individual editor components
   * can be agnostic and simply work with the values.
   */
  const handleSaveTraitDetail = useCallback(
    (detail: { value: any; year?: number }) => {
      setSaveError(undefined)

      switch (resolvedDataType) {
        case 'field':
          saveTaskData(
            {
              fieldDetails: [
                {
                  resourceId,
                  details: [
                    {
                      traitId: traitConfig.traitId,
                      result: detail.value,
                      year: firstRequirement?.year,
                    },
                  ],
                },
              ],
            },
            { onError: setSaveError }
          )
          break

        case 'user':
          saveTaskData(
            {
              userDetails: [
                {
                  traitId: traitConfig.traitId,
                  value: detail.value,
                  year: firstRequirement?.year,
                },
              ],
            },
            { onError: setSaveError }
          )
          break

        case 'workflow':
          saveTaskData(
            {
              wfDetails: [
                {
                  traitId: traitConfig.traitId,
                  value: detail.value,
                  year: firstRequirement?.year,
                },
              ],
            },
            { onError: setSaveError }
          )
          break
      }
    },
    [saveTaskData]
  )

  let EditorUi = <></>
  let hideTitle = false

  const traitStatus = taskData.data?.getTraitStatus(traitConfig.traitId)
  const params = traitStatus?.params

  switch (traitConfig.displayType) {
    case 'boolean':
      EditorUi = (
        <DataEntryRadioGroup
          traitConfig={traitConfig}
          detailRequirement={firstRequirement}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )
      break

    case 'text': {
      const options = params?.options as DataEntryOption[]
      if (params.display === 'tile') {
        EditorUi = (
          <DataEntryTilesSelector
            options={options}
            taskData={taskData}
            detailRequirement={firstRequirement}
            traitConfig={traitConfig}
            traitDetailValue={traitDetailValue}
            handleChangeDetail={handleSaveTraitDetail}
            immutable={traitDetail?.immutable}
            removeText={params?.removeText}
            selectText={params?.selectText}
          />
        )
      } else {
        if (params?.gate) {
          EditorUi = (
            <DataEntryGateGroup
              traitConfig={traitConfig}
              detailRequirement={firstRequirement}
              traitDetailValue={traitDetailValue}
              onChange={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        } else if (params?.multiChoice) {
          EditorUi = (
            <DataEntryCheckboxList
              options={options}
              taskData={taskData}
              detailRequirement={firstRequirement}
              traitConfig={traitConfig}
              traitDetailValue={traitDetailValue}
              handleChangeDetail={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        } else {
          EditorUi = (
            <DataEntryRadioGroup
              traitConfig={traitConfig}
              detailRequirement={firstRequirement}
              traitDetailValue={traitDetailValue}
              onChange={handleSaveTraitDetail}
              immutable={traitDetail?.immutable}
            />
          )
        }
      }

      break
    }

    case 'date':
      EditorUi = (
        <DatePickerTraitEditor
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitConfig}
          detailRequirement={firstRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )
      break

    case 'email':
      EditorUi = (
        <EmailDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitConfig}
          detailRequirement={firstRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
          saveError={saveError}
          ciboSourced={ciboSourced}
        />
      )
      break

    case 'longText':
      EditorUi = (
        <LongTextDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitConfig}
          detailRequirement={firstRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )
      break

    case 'number':
      EditorUi = (
        <NumberDataEntryField
          taskId={taskId}
          taskData={taskData}
          traitConfig={traitConfig as EngagementTaskTrait<ResourceDataType>}
          detailRequirement={firstRequirement}
          resourceId={resourceId}
          traitDetailValue={traitDetailValue}
          onChange={handleSaveTraitDetail}
          immutable={traitDetail?.immutable}
        />
      )
      break

    case 'yearlyCheckboxes':
      console.error(`yearlyCheckboxes does not support dataType '${resolvedDataType}'`)
      EditorUi = <></>
      break

    case 'file':
      if (!engagementId || !engagement.data) {
        EditorUi = <></>
      } else {
        EditorUi = (
          <DataEntryFileUpload
            taskId={taskId}
            taskData={taskData}
            traitConfig={traitConfig as EngagementTaskTrait<ResourceDataType>}
          />
        )
      }
      break
  }

  return (
    <Stack spacing={1} flex={1} data-testid={`dataEntry-editor-${traitConfig.traitId}`}>
      <Stack>
        {!hideTitle && <Markdown>{traitConfig.title}</Markdown>}

        {!!params?.furtherText && (
          <Markdown style={{ lineHeight: 1.66, fontSize: '0.875rem' }}>
            {engagement.data?.replaceCopyTokens(params.furtherText)}
          </Markdown>
        )}
      </Stack>

      {EditorUi}

      <Stack spacing={2}>
        {traitConfig.children?.map(traitConfig => {
          const childTraitDisplay = traitDisplay?.children?.find(
            propEq('traitId', traitConfig.traitId)
          )

          return (
            <SingleTargetTraitDisplays
              key={traitConfig.traitId}
              taskId={taskId}
              taskConfig={taskConfig}
              traitConfig={traitConfig}
              resourceId={resourceId}
              traitDisplay={childTraitDisplay}
              saveTaskData={saveTaskData}
              TaskEditorComponent={TaskEditorComponent}
              noSection
            />
          )
        })}
      </Stack>
    </Stack>
  )
}
